import { create } from "zustand";
import { useEffect, useState } from "react";
import {
  ACTION_DEFAULT,
  ACTION_MAX,
  ACTION_MIN,
  NOTICE_DEFAULT,
  NOTICE_MAX,
  NOTICE_MIN,
  clampColumnWidth,
  layoutColumns,
  type WorkflowColumns,
} from "./column-layout";

const NOTICE_WIDTH_KEY = "notice-width";
const NOTICE_COLLAPSED_KEY = "notice-collapsed";
const ACTION_WIDTH_KEY = "action-width";

function readWidth(key: string, fallback: number, min: number, max: number) {
  try {
    const raw = sessionStorage.getItem(key);
    if (raw === null) return fallback;
    const stored = Number(raw);
    return Number.isFinite(stored)
      ? clampColumnWidth(stored, min, max)
      : fallback;
  } catch {
    return fallback;
  }
}

function readCollapsed() {
  try {
    return sessionStorage.getItem(NOTICE_COLLAPSED_KEY) === "true";
  } catch {
    return false;
  }
}

function write(key: string, value: string) {
  try {
    sessionStorage.setItem(key, value);
  } catch {
    /* persistence is best-effort */
  }
}

interface WorkflowColumnStore extends WorkflowColumns {
  dragging: boolean;
  setNotice: (next: { collapsed: boolean; width: number }) => void;
  setNoticeCollapsed: (collapsed: boolean) => void;
  setActionWidth: (width: number) => void;
  setDragging: (dragging: boolean) => void;
  resetNotice: () => void;
  resetAction: () => void;
}

export const useWorkflowColumns = create<WorkflowColumnStore>((set) => ({
  noticeWidth: readWidth(NOTICE_WIDTH_KEY, NOTICE_DEFAULT, NOTICE_MIN, NOTICE_MAX),
  noticeCollapsed: readCollapsed(),
  actionWidth: readWidth(ACTION_WIDTH_KEY, ACTION_DEFAULT, ACTION_MIN, ACTION_MAX),
  dragging: false,
  setNotice: ({ collapsed, width }) => {
    write(NOTICE_WIDTH_KEY, String(width));
    write(NOTICE_COLLAPSED_KEY, String(collapsed));
    set({ noticeWidth: width, noticeCollapsed: collapsed });
  },
  setNoticeCollapsed: (collapsed) => {
    write(NOTICE_COLLAPSED_KEY, String(collapsed));
    set({ noticeCollapsed: collapsed });
  },
  setActionWidth: (width) => {
    write(ACTION_WIDTH_KEY, String(width));
    set({ actionWidth: width });
  },
  setDragging: (dragging) => set({ dragging }),
  resetNotice: () => {
    write(NOTICE_WIDTH_KEY, String(NOTICE_DEFAULT));
    write(NOTICE_COLLAPSED_KEY, "false");
    set({ noticeWidth: NOTICE_DEFAULT, noticeCollapsed: false });
  },
  resetAction: () => {
    write(ACTION_WIDTH_KEY, String(ACTION_DEFAULT));
    set({ actionWidth: ACTION_DEFAULT });
  },
}));

export function useViewportWidth() {
  const [width, setWidth] = useState(() =>
    typeof window === "undefined" ? 1440 : window.innerWidth,
  );
  useEffect(() => {
    const onResize = () => setWidth(window.innerWidth);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);
  return width;
}

export function useWorkflowColumnLayout() {
  const viewport = useViewportWidth();
  const columns = useWorkflowColumns();
  const layout = layoutColumns(viewport, columns);
  return {
    ...columns,
    ...layout,
    noticeUserCollapsed: columns.noticeCollapsed,
  };
}
